import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { tokenStore, verifySession, XanoError } from "@/lib/authTransport";
import type { User } from "@/lib/xano";
import { discardStartupRead, primeStartupRoute } from "./startupReads";
import { readSignIn } from "./twoFactorModel";

const initialToken = tokenStore.get();
if (initialToken) primeStartupRoute(initialToken);
const bootstrapStarted = Date.now();
const initialSession = initialToken && !window.location.hostname.endsWith(".pages.dev")
  ? verifySession(initialToken).then(user => ({ user, error: null }), error => ({ user: null, error }))
  : Promise.resolve({ user: null, error: null });

type AuthState = {
  user: User | null;
  status: "loading" | "authed" | "anon" | "error";
};

/**
 * What a password sign-in came to. An account with two-step sign-in on gets no session
 * from its password: it gets a challenge, and completeTwoFactor finishes with a code.
 */
export type SignInResult = { status: "signed_in" } | { status: "code_required"; challenge: string };

type AuthContextValue = AuthState & {
  login: (email: string, password: string) => Promise<SignInResult>;
  /** The second step: the challenge from a sign-in, and a code or recovery code. */
  completeTwoFactor: (challenge: string, code: string) => Promise<void>;
  signup: (args: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    invite_code: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, status: "loading" });
  const qc = useQueryClient();
  const generation = useRef(0);

  const restore = useCallback(async (bootstrap = false) => {
    const version = ++generation.current;
    const token = tokenStore.get();
    if (!token) {
      setState({ user: null, status: "anon" });
      return;
    }
    setState({ user: null, status: "loading" });
    try {
      const result = bootstrap && token === initialToken && Date.now() - bootstrapStarted < 15_000 ? await initialSession : null;
      if (result?.error) throw result.error;
      const user = result?.user ?? await verifySession(token);
      if (version !== generation.current || tokenStore.get() !== token) return;
      setState({ user, status: "authed" });
      performance.mark("bloxx:session-verified");
    } catch (error) {
      if (version !== generation.current || tokenStore.get() !== token) return;
      discardStartupRead();
      if (error instanceof XanoError && (error.status === 401 || error.status === 403)) {
        tokenStore.clear();
        qc.clear();
        setState({ user: null, status: "anon" });
      } else {
        setState({ user: null, status: "error" });
      }
    }
  }, [qc]);
  const refresh = useCallback(() => restore(), [restore]);

  useEffect(() => {
    void restore(true);
    return () => { generation.current++; };
  }, [restore]);

  const login = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      const version = ++generation.current;
      const { xano } = await import("@/lib/xano");
      const answer = readSignIn(await xano.auth.login(email, password));
      if (answer.kind === "code") return { status: "code_required", challenge: answer.challenge };
      if (version !== generation.current) return { status: "signed_in" };
      qc.clear();
      tokenStore.set(answer.token);
      await refresh();
      qc.invalidateQueries();
      return { status: "signed_in" };
    },
    [qc, refresh]
  );

  const completeTwoFactor = useCallback(
    async (challenge: string, code: string) => {
      const version = ++generation.current;
      const { twoFactor } = await import("@/lib/twoFactor");
      const answer = readSignIn(await twoFactor.verify(challenge, code));
      if (answer.kind !== "session" || version !== generation.current) return;
      qc.clear();
      tokenStore.set(answer.token);
      await refresh();
      qc.invalidateQueries();
    },
    [qc, refresh]
  );

  const signup = useCallback<AuthContextValue["signup"]>(
    async (args) => {
      const version = ++generation.current;
      const { xano } = await import("@/lib/xano");
      const res = await xano.auth.signup(args);
      if (version !== generation.current) return;
      generation.current++;
      qc.clear();
      tokenStore.set(res.authToken);
      if (res.user) {
        setState({ user: res.user, status: "authed" });
      } else {
        await refresh();
      }
      qc.invalidateQueries();
    },
    [qc, refresh]
  );

  const logout = useCallback(async () => {
    generation.current++;
    const token = tokenStore.get();
    tokenStore.clear();
    setState({ user: null, status: "anon" });
    qc.clear();
    try {
      const base = new URL(import.meta.env.VITE_XANO_AUTH_URL.replace(/\/?$/, "/"), window.location.origin);
      await fetch(new URL("auth/logout", base), { method: "POST", headers: { Authorization: `Bearer ${token ?? ""}` }, signal: AbortSignal.timeout(10000) });
    } catch {
      /* token may already be invalid; clear anyway */
    }
  }, [qc]);

  useEffect(() => {
    const changed = (event: StorageEvent) => {
      if (event.key === "bloxx_auth_token" || event.key === null) { discardStartupRead(); qc.clear(); void restore(); }
    };
    window.addEventListener("storage", changed);
    return () => window.removeEventListener("storage", changed);
  }, [qc, restore]);

  const value = useMemo(
    () => ({ ...state, login, completeTwoFactor, signup, logout, refresh }),
    [state, login, completeTwoFactor, signup, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
